// MY PROGRESS — field worker streaks + achievements. Field role only.
import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Flame, Trophy, Loader2, Check, ChevronLeft, HardHat, Tractor, Star, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CATS = ["ALL", "TIME", "JOBS", "PLANT", "SKILLS", "SAFETY & QA", "FIELDLINK"];

const fmtN = (n) => (n >= 1000 ? n.toLocaleString("en-NZ") : String(n));

function StreakRing({ value }) {
  const pct = Math.min((value % 30 || (value ? 30 : 0)) / 30, 1);
  const R = 84, C = 2 * Math.PI * R;
  return (
    <div className="relative w-52 h-52 mx-auto" data-testid="streak-ring">
      <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
        <circle cx="100" cy="100" r={R} fill="none" stroke="currentColor" className="text-white/10" strokeWidth="12" />
        <circle cx="100" cy="100" r={R} fill="none" stroke="#FF5F15" strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct)} className="transition-all duration-1000" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Flame className="w-6 h-6 text-[#FF5F15] mb-1" />
        <span className="font-display font-black text-5xl leading-none" data-testid="streak-current">{value}</span>
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 mt-1">Day streak</span>
      </div>
    </div>
  );
}

function Badge({ a, onClick, size = "" }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1.5 group" data-testid={`badge-${a.id}`}>
      <div className={`relative ${size || "w-24 h-24"}`}>
        <img src={a.image} alt={a.name} loading="lazy"
          className={`w-full h-full object-contain transition-transform group-active:scale-95 ${a.earned ? "" : "grayscale opacity-35"} ${a.rare && a.earned ? "drop-shadow-[0_0_14px_rgba(255,184,48,0.45)]" : a.earned ? "drop-shadow-[0_0_8px_rgba(255,95,21,0.25)]" : ""}`} />
        {!a.earned && <span className="absolute inset-0 flex items-center justify-center text-[9px] font-mono font-bold text-zinc-300 bg-black/20 rounded-full m-8">🔒</span>}
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wider text-center leading-tight max-w-[7.5rem]">{a.name}</span>
      {a.earned ? (
        <span className="text-[9px] text-emerald-400 font-mono">{a.earned_at ? new Date(a.earned_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "Earned"}</span>
      ) : (
        <div className="w-16">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#FF5F15]/70" style={{ width: `${Math.min((a.progress / a.target) * 100, 100)}%` }} />
          </div>
          <p className="text-[9px] font-mono text-zinc-500 text-center mt-0.5">{fmtN(a.progress)} / {fmtN(a.target)}</p>
        </div>
      )}
    </button>
  );
}

function BadgeSheet({ a, showcase, onShowcase, onClose }) {
  if (!a) return null;
  const inShowcase = showcase.includes(a.id);
  return (
    <div className="fixed inset-0 z-[80]" data-testid="badge-sheet">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" onClick={onClose} />
      <div className="absolute bottom-0 inset-x-0 bg-[#101620] border-t border-white/10 rounded-t-3xl px-6 pt-3 pb-8 max-w-md mx-auto animate-in slide-in-from-bottom duration-200">
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4" />
        <div className="flex flex-col items-center text-center">
          <img src={a.image} alt="" className={`w-32 h-32 object-contain ${a.earned ? "" : "grayscale opacity-40"}`} />
          <h3 className="font-display font-black uppercase tracking-tight text-lg mt-2">{a.name}</h3>
          <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#FF5F15]">{a.category}{a.rare ? " · RARE" : ""}</span>
          <p className="text-xs text-zinc-400 mt-2">{a.desc}</p>
          {a.note && <p className="text-[10px] text-zinc-500 italic mt-1.5">{a.note}</p>}
          {a.earned ? (
            <p className="text-[11px] text-emerald-400 font-mono mt-3" data-testid="badge-sheet-earned">
              Earned {a.earned_at ? new Date(a.earned_at).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" }) : ""}
            </p>
          ) : (
            <div className="w-full mt-3">
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-[#FF5F15]" style={{ width: `${Math.min((a.progress / a.target) * 100, 100)}%` }} />
              </div>
              <p className="text-[11px] font-mono text-zinc-400 mt-1">{fmtN(a.progress)} / {fmtN(a.target)}</p>
            </div>
          )}
          {a.earned && (
            <button onClick={() => onShowcase(a.id)}
              className={`mt-4 flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold uppercase tracking-wider border ${inShowcase ? "border-[#FF5F15] text-[#FF5F15] bg-[#FF5F15]/10" : "border-white/15 text-zinc-300"}`}
              data-testid="badge-showcase-toggle">
              <Star className={`w-3.5 h-3.5 ${inShowcase ? "fill-[#FF5F15]" : ""}`} />
              {inShowcase ? "On your profile" : "Showcase on profile"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Celebration({ item, onDone }) {
  if (!item) return null;
  const isStreak = item.kind === "streak";
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center" data-testid="celebration-overlay">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="relative flex flex-col items-center text-center px-8 animate-in zoom-in-75 fade-in duration-500">
        {isStreak ? (
          <Flame className="w-20 h-20 text-[#FF5F15] drop-shadow-[0_0_24px_rgba(255,95,21,0.7)] animate-pulse" />
        ) : (
          <img src={item.image} alt="" className="w-40 h-40 object-contain drop-shadow-[0_0_28px_rgba(255,184,48,0.55)]" />
        )}
        <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-[#FF5F15] mt-4">
          {isStreak ? `${item.value} day streak` : "Achievement unlocked"}
        </p>
        <h2 className="font-display font-black uppercase tracking-tight text-2xl mt-1">
          {isStreak ? (item.value >= 30 ? "Dialled in." : "On a roll.") : item.name}
        </h2>
        <p className="text-xs text-zinc-400 mt-1.5 max-w-[260px]">
          {isStreak ? `${item.value} scheduled workdays with your Fieldlink requirements sorted.` : item.desc}
        </p>
        <button onClick={onDone}
          className="mt-6 px-8 py-2.5 rounded-xl bg-[#FF5F15] text-white text-xs font-bold uppercase tracking-widest active:scale-95 transition-transform"
          data-testid="celebration-done">Nice</button>
      </div>
    </div>
  );
}

export default function MyProgress() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("streaks");
  const [cat, setCat] = useState("ALL");
  const [sheet, setSheet] = useState(null);
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    if (user?.role !== "field") return;
    (async () => {
      try {
        const { data: d } = await api.get("/progress/me");
        setData(d);
        const q = (d.new_unlocks || []).map((a) => ({ ...a, kind: "badge" }));
        if (d.streak_milestone) q.push({ kind: "streak", value: d.streak_milestone });
        setQueue(q);
        if (navigator.vibrate && q.length) navigator.vibrate(30);
      } catch { toast.error("Couldn't load your progress"); }
      finally { setLoading(false); }
    })();
  }, [user]);

  if (user?.role !== "field") return <Navigate to="/app" replace />;
  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#FF5F15]" /></div>;
  if (!data) return null;

  const s = data.summary;
  const dismissCelebration = async () => {
    const [head, ...rest] = queue;
    if (head?.kind === "streak") { try { await api.post("/progress/ack-milestone", { milestone: head.value }); } catch { /* noop */ } }
    setQueue(rest);
  };
  const toggleShowcase = async (id) => {
    const cur = data.showcase || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id].slice(-3);
    try {
      const { data: r } = await api.post("/progress/showcase", { badge_ids: next });
      setData((p) => ({ ...p, showcase: r.showcase }));
      toast.success(cur.includes(id) ? "Removed from profile" : "Showcased on your profile");
    } catch { toast.error("Couldn't update showcase"); }
  };

  const shown = (data.achievements || []).filter((a) => cat === "ALL" || a.category === cat);
  const earnedShown = shown.filter((a) => a.earned);
  const lockedShown = shown.filter((a) => !a.earned);

  return (
    <div className="text-zinc-100" data-testid="my-progress-page">
      {/* header */}
      <div className="flex items-center gap-2 mb-1">
        <Link to="/app" className="text-zinc-400 active:text-[#FF5F15]" data-testid="progress-back"><ChevronLeft className="w-5 h-5" /></Link>
        <h1 className="font-display font-black uppercase tracking-tight text-xl">Your Fieldlink</h1>
      </div>
      <p className="text-[11px] text-zinc-500 mb-4 ml-7">Your work. Your experience. Your progress.</p>

      {/* summary tiles */}
      <div className="grid grid-cols-4 gap-2 mb-5" data-testid="progress-summary">
        {[
          { icon: Flame, v: s.streak_current, l: "Day streak", c: "text-[#FF5F15]" },
          { icon: Trophy, v: s.achievements_earned, l: "Badges", c: "text-amber-400" },
          { icon: HardHat, v: s.jobs, l: "Jobs built", c: "text-sky-400" },
          { icon: Tractor, v: Math.round(s.machine_hours), l: "Machine hrs", c: "text-emerald-400" },
        ].map(({ icon: I, v, l, c }) => (
          <div key={l} className="bg-[#101620] border border-white/[0.07] rounded-2xl px-2 py-3 flex flex-col items-center">
            <I className={`w-4 h-4 ${c} mb-1`} />
            <span className="font-display font-black text-lg leading-none">{fmtN(v)}</span>
            <span className="text-[8px] font-bold uppercase tracking-wider text-zinc-500 mt-1 text-center">{l}</span>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div className="flex bg-[#101620] border border-white/[0.07] rounded-2xl p-1 mb-5" data-testid="progress-tabs">
        {[["streaks", "🔥 Streaks"], ["achievements", "🏆 Achievements"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 py-2 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-colors ${tab === k ? "bg-[#FF5F15] text-white" : "text-zinc-400"}`}
            data-testid={`progress-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === "streaks" && (
        <div className="space-y-4" data-testid="streaks-tab">
          <div className="bg-[#101620] border border-white/[0.07] rounded-2xl p-6">
            <StreakRing value={s.streak_current} />
            <p className="text-center font-display font-black uppercase tracking-tight mt-4">
              {s.streak_current > 0 ? `Keep it going, ${data.first_name}!` : `Let's get started, ${data.first_name}!`}
            </p>
            <p className="text-center text-[11px] text-zinc-500 mt-1">
              {s.streak_current > 0 ? "You're building great habits every day." : "Complete today's requirements to start a streak."}
            </p>
            <p className="text-center text-[10px] font-mono text-zinc-400 mt-2">Longest streak: <b className="text-[#FF5F15]">{s.streak_longest} days</b></p>
          </div>

          {/* today */}
          <div className="bg-[#101620] border border-white/[0.07] rounded-2xl p-4" data-testid="today-card">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Today</span>
              <span className="text-[11px] font-mono font-bold text-[#FF5F15]">{data.today.complete} / {data.today.total} complete</span>
            </div>
            {data.today.items.map((i) => (
              <div key={i.key} className="flex items-center gap-2.5 py-1.5" data-testid={`today-item-${i.key}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center ${i.done ? "bg-emerald-500/20 text-emerald-400" : "border border-white/20 text-transparent"}`}>
                  <Check className="w-3 h-3" />
                </span>
                <span className={`text-xs font-semibold ${i.done ? "" : "text-zinc-400"}`}>{i.label}</span>
              </div>
            ))}
            {data.today.complete < data.today.total ? (
              <p className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mt-1.5">
                {data.today.total - data.today.complete} more to keep your streak 🔥
              </p>
            ) : (
              <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-wider mt-1.5">Everything is sorted today ✓</p>
            )}
          </div>

          {/* habit streaks */}
          {data.habits.map((h) => {
            const done = h.current >= h.target;
            return (
              <div key={h.key} className="bg-[#101620] border border-white/[0.07] rounded-2xl p-4" data-testid={`habit-${h.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider">{h.label}</span>
                  <span className={`text-[11px] font-mono font-bold ${done ? "text-emerald-400" : "text-zinc-300"}`}>
                    {h.current} / {h.target}{done ? " ✓" : ""}
                  </span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-2">
                  <div className={`h-full ${done ? "bg-emerald-500" : "bg-[#FF5F15]"} transition-all`} style={{ width: `${Math.min((h.current / h.target) * 100, 100)}%` }} />
                </div>
                <p className="text-[10px] text-zinc-500 mt-1.5">{h.desc}</p>
              </div>
            );
          })}

          <div className="bg-[#101620] border border-white/[0.07] rounded-2xl p-4 flex items-start gap-3">
            <Trophy className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider">Why streaks?</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">Good habits keep you, your crew and the job running smoothly. Weekends, leave and days you're not scheduled never break your streak.</p>
            </div>
          </div>
        </div>
      )}

      {tab === "achievements" && (
        <div data-testid="achievements-tab">
          <p className="text-[11px] text-zinc-500 mb-3">Recognising the work you're proud of. <span className="font-mono font-bold text-zinc-300">{s.achievements_earned} / {s.achievements_total} earned</span></p>
          <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4 -mx-1 px-1" data-testid="badge-filters">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cat === c ? "bg-[#FF5F15] border-[#FF5F15] text-white" : "border-white/15 text-zinc-400"}`}
                data-testid={`badge-filter-${c.replace(/[^A-Z]/g, "")}`}>{c}</button>
            ))}
          </div>
          {earnedShown.length > 0 && (
            <>
              <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-3">Earned</p>
              <div className="grid grid-cols-3 gap-x-2 gap-y-5 mb-6" data-testid="earned-grid">
                {earnedShown.map((a) => <Badge key={a.id} a={a} onClick={() => setSheet(a)} />)}
              </div>
            </>
          )}
          {lockedShown.length > 0 && (
            <>
              <p className="text-[9px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-3">Locked</p>
              <div className="grid grid-cols-3 gap-x-2 gap-y-5" data-testid="locked-grid">
                {lockedShown.map((a) => <Badge key={a.id} a={a} onClick={() => setSheet(a)} />)}
              </div>
            </>
          )}
        </div>
      )}

      <BadgeSheet a={sheet} showcase={data.showcase || []} onShowcase={toggleShowcase} onClose={() => setSheet(null)} />
      <Celebration item={queue[0]} onDone={dismissCelebration} />
    </div>
  );
}
